import { useCallback, useEffect, useMemo, useState } from 'react'
import { GripVertical, Pencil, Clock3 } from 'lucide-react'
import { apiMutate, Drawer } from './primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { iconOf, STATE_DOT, useCatalog, type CatalogEntry } from '../hubs/InfraCatalog.js'

/**
 * PERSONNALISATION DU CATALOGUE (LOT 8.1) — Paramètres → Services.
 *
 * Trois axes indépendants, éditables SANS bouton « Enregistrer » local :
 * l'écart avec le snapshot serveur alimente la Floating Save Bar globale
 * (règle produit LOT 5.5.1). L'état FINAL est audité, jamais chaque drag.
 */

interface Editable {
  display_name: string
  description: string | null
  category: string
  prominence: CatalogEntry['prominence']
  criticality: CatalogEntry['criticality']
  show_in_global_status: boolean
  affects_global_health: boolean
  show_in_infrastructure: boolean
  display_order: number
  is_enabled: boolean
}

const editableOf = (e: CatalogEntry): Editable => ({
  display_name: e.display_name,
  description: e.description,
  category: e.category,
  prominence: e.prominence,
  criticality: e.criticality,
  show_in_global_status: e.show_in_global_status,
  affects_global_health: e.affects_global_health,
  show_in_infrastructure: e.show_in_infrastructure,
  display_order: e.display_order,
  is_enabled: e.is_enabled,
})

const PROM_LABELS: Record<CatalogEntry['prominence'], string> = {
  HERO: 'Principal', STANDARD: 'Standard', COMPACT: 'Compact', HIDDEN: 'Masqué',
}
const CRIT_LABELS: Record<CatalogEntry['criticality'], string> = {
  CRITICAL: 'Critique', IMPORTANT: 'Important', NORMAL: 'Normal', AUXILIARY: 'Auxiliaire',
}

export function CatalogEditor() {
  const server = useCatalog(60_000)
  const [draft, setDraft] = useState<Map<string, Editable> | null>(null)
  const [modal, setModal] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  // Snapshot serveur → brouillon local (uniquement quand PAS d'édition en cours).
  useEffect(() => {
    if (server && draft === null) {
      setDraft(new Map(server.entries.map((e) => [e.slug, editableOf(e)])))
    }
  }, [server, draft])

  const serverMap = useMemo(
    () => new Map((server?.entries ?? []).map((e) => [e.slug, editableOf(e)])),
    [server],
  )

  const changes = useMemo(() => {
    if (!draft) return []
    const out: ({ slug: string } & Partial<Editable>)[] = []
    for (const [slug, d] of draft) {
      const s = serverMap.get(slug)
      if (!s) continue
      const diff: Partial<Editable> = {}
      for (const k of Object.keys(d) as (keyof Editable)[]) {
        if (d[k] !== s[k]) (diff as Record<string, unknown>)[k] = d[k]
      }
      if (Object.keys(diff).length > 0) out.push({ slug, ...diff })
    }
    return out
  }, [draft, serverMap])

  const save = useCallback(async (): Promise<true | string> => {
    const r = await apiMutate<{ ok?: boolean; message?: string }>('PUT', '/api/v1/infrastructure/catalog', { updates: changes })
    if (r.status !== 200) return r.data.message ?? 'Enregistrement impossible.'
    setDraft(null) // resynchronise depuis le serveur
    return true
  }, [changes])

  useSaveBar({
    dirty: changes.length > 0,
    count: changes.length,
    onSave: save,
    onCancel: () => setDraft(null),
    successMessage: 'Catalogue mis à jour.',
  })

  const upd = (slug: string, patch: Partial<Editable>) => {
    setDraft((prev) => {
      const next = new Map(prev)
      const cur = next.get(slug)
      if (cur) next.set(slug, { ...cur, ...patch })
      return next
    })
  }

  // Drag & drop : réordonne dans la liste triée, recalcule display_order ×10.
  const ordered = useMemo(() => {
    if (!server || !draft) return []
    return [...server.entries]
      .map((e) => ({ e, d: draft.get(e.slug) ?? editableOf(e) }))
      .sort((a, b) => a.d.display_order - b.d.display_order || a.e.slug.localeCompare(b.e.slug))
  }, [server, draft])

  const dropOn = (targetSlug: string) => {
    if (!dragging || dragging === targetSlug) return
    const slugs = ordered.map((x) => x.e.slug)
    const from = slugs.indexOf(dragging)
    const to = slugs.indexOf(targetSlug)
    if (from < 0 || to < 0) return
    slugs.splice(to, 0, ...slugs.splice(from, 1))
    setDraft((prev) => {
      const next = new Map(prev)
      slugs.forEach((slug, i) => {
        const cur = next.get(slug)
        if (cur && cur.display_order !== i * 10) next.set(slug, { ...cur, display_order: i * 10 })
      })
      return next
    })
    setDragging(null)
  }

  const cats = server?.categories ?? []
  const modalEntry = modal ? server?.entries.find((e) => e.slug === modal) ?? null : null
  const modalDraft = modal && draft ? draft.get(modal) ?? null : null

  return (
    <section className="lc-panel gd-panel ad-catalog" aria-label="Catalogue Infrastructure">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">Catalogue Infrastructure</h2>
        <span className="lc-hint">
          Affichage, ordre et criticité — un service masqué reste supervisé.
        </span>
      </header>
      <div className="lc-panel__body lc-panel__body--flush">
        {ordered.map(({ e, d }) => {
          const Icon = iconOf(e.icon)
          return (
            <div
              key={e.slug}
              className={`ad-catalog__row ${dragging === e.slug ? 'ad-catalog__row--drag' : ''}`}
              draggable
              onDragStart={() => setDragging(e.slug)}
              onDragOver={(ev) => ev.preventDefault()}
              onDrop={() => dropOn(e.slug)}
              onDragEnd={() => setDragging(null)}
            >
              <span className="ad-catalog__grip" aria-hidden="true"><GripVertical size={15} /></span>
              <span className="infra-row__icon" aria-hidden="true"><Icon size={15} /></span>
              <span className="lc-stack ad-catalog__main">
                <span className="ad-catalog__name">
                  {d.display_name}
                  {!d.is_enabled ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Désactivé</span> : null}
                </span>
                <span className="lc-hint">
                  {cats.find((c) => c.key === d.category)?.label ?? d.category} · {PROM_LABELS[d.prominence]} · {CRIT_LABELS[d.criticality]}
                  {d.show_in_global_status ? ' · Global Status' : ''}
                  {d.affects_global_health ? ' · pèse sur l’état global' : ''}
                </span>
              </span>
              <span className={`lc-dot ${STATE_DOT[e.state] ?? ''}`} aria-hidden="true" data-tip={e.state_label} />
              <button className="lc-btn lc-btn--ghost" onClick={() => setModal(e.slug)}>
                <Pencil size={13} aria-hidden="true" /> Modifier
              </button>
            </div>
          )
        })}
        {!server ? <p className="lc-hint" style={{ padding: 'var(--sp-4)' }}>Chargement du catalogue…</p> : null}
      </div>

      <Drawer open={modalEntry !== null} onClose={() => setModal(null)} title={modalEntry?.display_name ?? ''}>
        {modalEntry && modalDraft ? (
          <div className="ad-catalog__form">
            <label className="adm-field">
              <span>Nom affiché</span>
              <input value={modalDraft.display_name} onChange={(ev) => upd(modalEntry.slug, { display_name: ev.target.value })} />
            </label>
            <label className="adm-field">
              <span>Description</span>
              <input value={modalDraft.description ?? ''} placeholder="Description courte"
                onChange={(ev) => upd(modalEntry.slug, { description: ev.target.value || null })} />
            </label>
            <label className="adm-field">
              <span>Catégorie</span>
              <select value={modalDraft.category} onChange={(ev) => upd(modalEntry.slug, { category: ev.target.value })}>
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>

            <fieldset className="adm-field">
              <span>Affichage</span>
              <div className="ad-catalog__radios" role="radiogroup">
                {(Object.keys(PROM_LABELS) as CatalogEntry['prominence'][]).map((p) => (
                  <button key={p} role="radio" aria-checked={modalDraft.prominence === p}
                    className={`lc-seg__btn ${modalDraft.prominence === p ? 'lc-seg__btn--on' : ''}`}
                    onClick={() => upd(modalEntry.slug, { prominence: p })}>
                    {PROM_LABELS[p]}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="adm-field">
              <span>Criticité</span>
              <select value={modalDraft.criticality}
                onChange={(ev) => upd(modalEntry.slug, { criticality: ev.target.value as CatalogEntry['criticality'] })}>
                {(Object.keys(CRIT_LABELS) as CatalogEntry['criticality'][]).map((c) => (
                  <option key={c} value={c}>{CRIT_LABELS[c]}</option>
                ))}
              </select>
            </label>

            <label className="ad-toggle">
              <input type="checkbox" checked={modalDraft.show_in_global_status}
                onChange={(ev) => upd(modalEntry.slug, { show_in_global_status: ev.target.checked })} />
              <span className="lc-stack">
                <span>Ligne individuelle dans le Global Status</span>
                <span className="lc-hint">Sinon, compté dans un groupe (« Services techniques 6/7 »).</span>
              </span>
            </label>
            <label className="ad-toggle">
              <input type="checkbox" checked={modalDraft.affects_global_health}
                onChange={(ev) => upd(modalEntry.slug, { affects_global_health: ev.target.checked })} />
              <span className="lc-stack">
                <span>Pèse sur l’état global du club</span>
                <span className="lc-hint">Indépendant de la visibilité — un service masqué peut dégrader l’état.</span>
              </span>
            </label>
            <label className="ad-toggle">
              <input type="checkbox" checked={modalDraft.show_in_infrastructure}
                onChange={(ev) => upd(modalEntry.slug, { show_in_infrastructure: ev.target.checked })} />
              <span className="lc-stack">
                <span>Afficher sur la page Infrastructure</span>
                <span className="lc-hint">Reste trouvable dans « Tous les services » et supervisé.</span>
              </span>
            </label>

            {/* Aperçu dynamique du rendu */}
            <div className="adm-field">
              <span>Aperçu</span>
              <div className={`infra-card infra-card--${modalEntry.state.toLowerCase()} ad-catalog__preview ${modalDraft.prominence === 'COMPACT' || modalDraft.prominence === 'HIDDEN' ? 'ad-catalog__preview--compact' : ''}`}>
                <div className="infra-card__head">
                  {(() => { const I = iconOf(modalEntry.icon); return <span className="infra-card__icon"><I size={16} aria-hidden="true" /></span> })()}
                  <span className="infra-card__name">{modalDraft.display_name}</span>
                </div>
                <div className="infra-card__state">
                  <span className={`lc-dot ${STATE_DOT[modalEntry.state] ?? ''}`} aria-hidden="true" />
                  {modalEntry.state_label}
                </div>
                {modalDraft.prominence === 'HERO' && modalEntry.headline ? <div className="lc-hint">{modalEntry.headline}</div> : null}
                {modalDraft.prominence === 'HIDDEN'
                  ? <div className="lc-hint">Masqué du hub — visible dans « Tous les services ».</div>
                  : <div className="lc-hint infra-card__fresh"><Clock3 size={11} aria-hidden="true" /> Actualisé en continu</div>}
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </section>
  )
}
